import React from "react";
import { Link } from "react-router-dom";
import { Check, Quote, ArrowRight } from "lucide-react";
import Reveal from "../components/Reveal";
import {
  IMAGES,
  SERVICE_INTRO,
  SIGNATURE_BENEFITS,
  ADDITIONAL_PROGRAMS,
  SERVICE_TESTIMONIAL,
  CONTACT_TESTIMONIALS,
} from "../mock";

const SERVICE_CLIENT_TESTIMONIALS = [CONTACT_TESTIMONIALS[1], CONTACT_TESTIMONIALS[2]];

function PageHeader({ kicker, title }) {
  return (
    <section className="bg-gradient-to-b from-[#effaf9] to-white pt-16 pb-10 md:pt-20 md:pb-14">
      <div className="max-w-4xl mx-auto px-5 text-center">
        <Reveal>
          <p className="text-xs tracking-[0.3em] uppercase text-[var(--teal-dark)]">{kicker}</p>
          <h1 className="font-serif-brand text-5xl md:text-6xl text-purple mt-3">{title}</h1>
          <div className="brand-rule mx-auto mt-5" />
        </Reveal>
      </div>
    </section>
  );
}

export default function Services() {
  return (
    <div>
      <PageHeader kicker="Holistic Intuitive Nutrition" title="Why HIN Living?" />

      {/* Intro */}
      <section className="max-w-4xl mx-auto px-5 py-4 md:py-8">
        <Reveal>
          <p className="text-neutral-600 leading-relaxed text-center">{SERVICE_INTRO.body}</p>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6 mt-10">
          <Reveal>
            <div className="h-full bg-[#f7fbfa] rounded-lg p-7 border border-neutral-100">
              <h3 className="text-purple font-semibold tracking-wide-sm uppercase text-sm mb-3">
                Red Light Therapy
              </h3>
              <p className="text-neutral-600 leading-relaxed text-[0.95rem]">{SERVICE_INTRO.redlight}</p>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="h-full bg-[#f7fbfa] rounded-lg p-7 border border-neutral-100">
              <h3 className="text-purple font-semibold tracking-wide-sm uppercase text-sm mb-3">
                PEMF Therapy
              </h3>
              <p className="text-neutral-600 leading-relaxed text-[0.95rem]">{SERVICE_INTRO.pemf}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Signature program */}
      <section className="bg-[var(--teal)] py-16 md:py-20 mt-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <p className="text-white/85 tracking-[0.3em] uppercase text-xs mb-4">Signature Program</p>
            <h2 className="font-serif-brand text-white text-4xl md:text-5xl leading-tight">
              Your Health is Your Wealth Wellness Program
            </h2>
            <p className="text-white/90 mt-6 leading-relaxed max-w-2xl mx-auto">
              A comprehensive program designed to empower you on your journey to optimal health,
              ensuring a harmonious balance of mind, body, and spirit, and incorporating healthy
              eating habits.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-5xl mx-auto px-5 py-16 md:py-24">
        <div className="space-y-6">
          {SIGNATURE_BENEFITS.map((b, i) => (
            <Reveal key={b.n} delay={i * 80}>
              <div className="flex gap-6 items-start bg-white rounded-lg border border-neutral-100 shadow-sm hover:shadow-lg transition-shadow duration-300 p-6 md:p-8">
                <div className="flex-shrink-0 w-14 h-14 rounded-full bg-[#effaf9] text-[var(--teal-dark)] flex items-center justify-center font-serif-brand text-2xl font-semibold">
                  {b.n}
                </div>
                <div>
                  <h3 className="text-purple font-semibold tracking-wide-sm uppercase text-sm md:text-base mb-2">
                    {b.title}
                  </h3>
                  <p className="text-neutral-600 leading-relaxed">{b.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-12">
          <div className="bg-[#f7fbfa] rounded-lg p-8 md:p-12 border border-neutral-100 relative">
            <Quote className="text-[var(--teal)] mb-4" size={36} />
            <p className="text-neutral-600 italic leading-relaxed text-lg">{SERVICE_TESTIMONIAL}</p>
          </div>
        </Reveal>

        <Reveal className="text-center mt-12">
          <Link to="/book" className="btn-brand btn-teal">
            Enroll Today <ArrowRight size={16} />
          </Link>
        </Reveal>
      </section>

      {/* Additional programs */}
      <section className="bg-[#f7fbfa] py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-5">
          <Reveal className="text-center mb-14">
            <h2 className="font-serif-brand text-4xl md:text-5xl text-purple">
              Additional Programs on Offer
            </h2>
            <div className="brand-rule mx-auto mt-4" />
          </Reveal>
          <div className="grid md:grid-cols-2 gap-8">
            {ADDITIONAL_PROGRAMS.map((p, i) => (
              <Reveal key={p.title} delay={i * 120}>
                <div className="h-full bg-white rounded-lg shadow-sm hover:shadow-xl transition-shadow duration-300 border border-neutral-100 p-8 flex flex-col">
                  <h3 className="font-serif-brand text-2xl md:text-3xl text-purple mb-3">{p.title}</h3>
                  <p className="text-neutral-600 leading-relaxed mb-6">{p.intro}</p>
                  <p className="text-xs tracking-[0.2em] uppercase text-[var(--teal-dark)] mb-3">
                    Program Components
                  </p>
                  <ul className="space-y-3 flex-1">
                    {p.components.map((c) => (
                      <li key={c} className="flex items-start gap-3 text-neutral-700">
                        <Check size={18} className="text-[var(--teal)] flex-shrink-0 mt-0.5" />
                        <span className="text-[0.95rem]">{c}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/book"
                    className="btn-brand btn-purple mt-8 w-full"
                  >
                    More Information
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* What clients say */}
      <section className="max-w-5xl mx-auto px-5 py-16 md:py-20">
        <Reveal className="text-center mb-12">
          <h2 className="font-serif-brand text-4xl md:text-5xl text-purple">What Clients Say</h2>
          <div className="brand-rule mx-auto mt-4" />
        </Reveal>
        <div className="grid md:grid-cols-2 gap-8">
          {SERVICE_CLIENT_TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 120}>
              <div className="h-full bg-white rounded-lg border border-neutral-100 shadow-sm hover:shadow-xl transition-shadow duration-300 p-8 flex flex-col">
                <div className="flex items-center gap-4 mb-5">
                  <img
                    src={t.image}
                    alt={t.name}
                    className="w-14 h-14 rounded-full object-cover ring-2 ring-[var(--teal)]"
                  />
                  <Quote className="text-[var(--teal)] ml-auto" size={26} />
                </div>
                <p className="text-neutral-600 italic leading-relaxed flex-1">{t.quote}</p>
                <p className="mt-6 text-purple font-semibold tracking-wide-sm uppercase text-sm">
                  {t.name}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Closing */}
      <section className="max-w-5xl mx-auto px-5 py-16 md:py-20">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <Reveal>
            <img src={IMAGES.servicesHero} alt="Wellness" className="rounded-lg shadow-xl w-full object-cover" />
          </Reveal>
          <Reveal delay={120}>
            <h2 className="font-serif-brand text-3xl md:text-4xl text-purple leading-tight">
              Invest in your wealth, your health!
            </h2>
            <div className="brand-rule mt-4 mb-6" />
            <p className="text-neutral-600 leading-relaxed">
              Embark on a transformative journey where your health becomes the cornerstone of your
              wealth. Invest in yourself, cultivate resilience, and discover the profound impact of
              holistic well being on every aspect of your life.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/book" className="btn-brand btn-teal">
                Get in touch with Coach Kam
              </Link>
              <Link to="/astrology" className="btn-brand btn-outline-teal">
                Astrology
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
